import type { TBTStatusBadgeDisplayTypes } from "@btnet/core-components/BTStatusBadge";

import { isQueryEdit } from "./results/resultsDisplay";
import type { SearchDialogRowKind } from "./rows/searchDialogRow.types";
import type { ISearchTypeVerdict, SearchTypeDecision } from "./search/search.types";
import type { AskArmedBy } from "./searchDialog.learn";

/**
 * Semantic polarity for a status chip or a stat tile. A token, never a color, so
 * the mapping below owns rendering and a server can send polarity without
 * sending a hex value that would break in dark mode.
 */
export type AgentTone = "neutral" | "positive" | "negative" | "warning";

const TONE_DISPLAY_TYPE: Record<AgentTone, TBTStatusBadgeDisplayTypes> = {
    neutral: "default",
    positive: "success",
    negative: "danger",
    warning: "warning",
};

export function toneToDisplayType(tone: AgentTone): TBTStatusBadgeDisplayTypes {
    return TONE_DISPLAY_TYPE[tone];
}

/**
 * Which of the dialog's two views is on screen. Derived from the query alone,
 * never set by a caller. Arming Ask changes what Enter does, not what renders.
 */
export type SearchDialogView = { kind: "browse" } | { kind: "results"; query: string };

export interface IResolveViewInput {
    /** The live input value. */
    query: string;
}

export interface IResolveAskModeInput {
    query: string;
    /**
     * The search service's verdict on what is typed, or null until one
     * applies (see {@link verdictFor}).
     */
    searchType: SearchTypeDecision | null;
}

/**
 * Whether the search service judges the query to want the agent, which offers
 * the Ask row. The dialog also offers it when the search left nothing to open.
 * The row is highlighted when it appears, so Enter asks; the footer says which.
 */
export function resolveAskMode(input: IResolveAskModeInput): boolean {
    return resolveAskArmedBy(input) !== null;
}

/**
 * What put the Ask row up, or null when there is none. The same rules as
 * {@link resolveAskMode}, which reads this, so the two cannot disagree.
 *
 * An `auto_open` verdict shows the row like `nudge`: the dialog never opens
 * the agent unasked.
 */
export function resolveAskArmedBy(input: IResolveAskModeInput): AskArmedBy | null {
    if (input.query.trim().length === 0) {
        return null;
    }
    return input.searchType === "nudge" || input.searchType === "auto_open" ? "auto" : null;
}

/**
 * The service's verdict as it applies to what is typed. One made for an
 * earlier form of the query (typed on from, or backspaced off) stands until
 * the search for the new text sends its own, so Ask does not drop out and
 * back in on every keystroke of a question. It stands only while that search
 * runs: one that ends without a verdict (classifier off, timed out, failed)
 * leaves the new text with none. After a clear or a new word it is about some
 * other query, so none applies.
 *
 * @param liveSearchSettled The search for `liveKey` has ended.
 */
export function verdictFor(
    verdict: ISearchTypeVerdict | null,
    liveKey: string,
    liveSearchSettled: boolean,
): SearchTypeDecision | null {
    if (verdict === null) {
        return null;
    }
    if (verdict.query === liveKey) {
        return verdict.decision;
    }
    return !liveSearchSettled && isQueryEdit(verdict.query, liveKey) ? verdict.decision : null;
}

/** What a bare Enter does right now. `none` is a real state: nothing to open, ask or retry. */
export type SearchDialogEnterAction = "open" | "ask" | "search" | "retry" | "none";

export interface IResolveEnterActionInput {
    /** The highlighted row's kind, or null while nothing holds the highlight. */
    highlightedKind: SearchDialogRowKind | null;
    hasQuery: boolean;
    /** The search on screen failed in a way another try could fix. */
    canRetry: boolean;
}

/** Mirrors the dialog's Enter handler, so the footer can say what Enter will do. */
export function resolveEnterAction(input: IResolveEnterActionInput): SearchDialogEnterAction {
    if (input.highlightedKind === "record") {
        return "open";
    }
    if (input.highlightedKind === "prompt" || input.highlightedKind === "ask") {
        return "ask";
    }
    if (input.highlightedKind === "search") {
        return "search";
    }
    return input.hasQuery && input.canRetry ? "retry" : "none";
}

/** Derives the current view. See {@link SearchDialogView} for why this is derived. */
export function resolveView(input: IResolveViewInput): SearchDialogView {
    // Cleared, or freshly opened; either way the user is browsing.
    if (input.query.trim().length === 0) {
        return { kind: "browse" };
    }

    return { kind: "results", query: input.query };
}
